import type { DriverDTO, SortOrder, UUID, VehicleDTOV2, VehicleSize } from '@cobliteam/common-types';

export interface Location {
  lat: number;
  lng: number;
}

export interface Geofence {
  geofenceCategory: string;
  geofenceId: string;
  geofenceName: string;
}

interface EventTimeAndLocation {
  startTime: string;
  location?: Location;
}

interface TripsAndStopsAggregation {
  drivenDistanceInKms: number;
  drivenTimeInMinutes: number;
  idleEngineTimeInMinutes: number;
  engineOnTimeInMinutes: number;
  averageDailyEngineOnTimeInMinutes: number;
  stopsCount: number;
  averageDailyStopsCount: number;
  totalStoppedTimeInMinutes: number;
  stopTimeAverageInMinutes: number;
  activeDaysCount: number;
  inactiveDaysCount: number;
}

export interface DriverTripsAndStopsAggregation extends TripsAndStopsAggregation {
  driver: DriverDTO;
}

export interface VehicleTripsAndStopsAggregation extends TripsAndStopsAggregation {
  vehicle: VehicleDTOV2;
}

export interface TripAndStopEvent {
  cobliId: string;
  vehicle: VehicleDTOV2;
  driver?: DriverDTO;
  startLocation?: Location;
  startTime?: string;
  drivenDistanceInKms?: number;
  tripDurationInMinutes?: number;
  endLocation?: Location;
  endTime?: string;
  stopGeofence?: Geofence;
  stopDurationInMinutes?: number;
  idleEngineDurationInMinutes?: number;
  totalCost: number;
}

export interface DailyTripsAndStopsAggregation {
  aggregationDate: string;
  drivenDistanceInKms: number;
  drivenTimeInMinutes: number;
  idleEngineTimeInMinutes: number;
  engineOnTimeInMinutes: number;
  stopsCount: number;
  totalStoppedTimeInMinutes: number;
  stopTimeAverageInMinutes: number;
  firstTrip?: EventTimeAndLocation;
  lastStop?: EventTimeAndLocation;
}

export interface VehicleDailyTripsAndStopsAggregation extends DailyTripsAndStopsAggregation {
  vehicle: VehicleDTOV2;
}

export interface DriverDailyTripsAndStopsAggregation extends DailyTripsAndStopsAggregation {
  driver: DriverDTO;
}

export interface TripsAndStopsResponse {
  count: number;
  rows: DriverTripsAndStopsAggregation[] | VehicleTripsAndStopsAggregation[] | TripAndStopEvent[] | VehicleDailyTripsAndStopsAggregation[] | DriverDailyTripsAndStopsAggregation[];
}

export enum WeekDay {
  Monday = 'MON',
  Tuesday = 'TUE',
  Wednesday = 'WED',
  Thursday = 'THU',
  Friday = 'FRI',
  Saturday = 'SAT',
  Sunday = 'SUN',
}

export const weekDayValue = {
  [WeekDay.Sunday]: 1,
  [WeekDay.Monday]: 2,
  [WeekDay.Tuesday]: 3,
  [WeekDay.Wednesday]: 4,
  [WeekDay.Thursday]: 5,
  [WeekDay.Friday]: 6,
  [WeekDay.Saturday]: 7,
};

export enum TripsAndStopsReportType {
  Aggregated = 'PERIODICAL',
  Detailed = 'EVENTS',
  Daily = 'DAILY',
}

export enum TripsAndStopsReportFormat {
  Excel = 'EXCEL',
}

export enum TripsAndStopsReportAggregatedBy {
  Vehicle = 'VEHICLE',
  Driver = 'DRIVER',
}

interface BaseTripsAndStopsRequest {
  startDate: string;
  endDate: string;
  startHour?: string;
  endHour?: string;
  weekDays?: WeekDay[];
  maxStopTimeInMinutes?: number;
  searchQuery?: string;
  sortOrder?: SortOrder;
  sortColumn?: string;
}

export interface TripsAndStopsReportRequest extends BaseTripsAndStopsRequest {
  reportFormat: TripsAndStopsReportFormat;
  wantedVehicleIds?: UUID[];
}

export interface TripsAndStopsEmailReportRequest extends TripsAndStopsReportRequest {
  destinationEmails: string[];
  wantedDriverIds?: UUID[];
  wantedGroupIds?: UUID[];
  aggregatedBy?: TripsAndStopsReportAggregatedBy;
  reportType: TripsAndStopsReportType;
}

export interface VehicleAggregationTripsAndStopsRequest extends BaseTripsAndStopsRequest {
  wantedIds?: UUID[];
  wantedVehicleSizes?: VehicleSize[];
  wantedVehicleTags?: string[];
  wantedGroupIds?: UUID[];
}

export interface DriverAggregationTripsAndStopsRequest extends BaseTripsAndStopsRequest {
  wantedIds?: UUID[];
}

export type SortColumnEvents =
  | 'vehicleLicensePlate'
  | 'driverName'
  | 'drivenDistanceInKms'
  | 'startTime'
  | 'endTime'
  | 'totalCost'
  | 'stopDurationInMinutes'
  | 'tripDurationInMinutes';

export interface EventsTripsAndStopsRequest extends BaseTripsAndStopsRequest {
  wantedDriverIds?: UUID[];
  wantedVehicleIds?: UUID[];
  wantedVehicleSizes?: VehicleSize[];
  wantedVehicleTags?: string[];
  wantedGroupIds?: UUID[];
  sortColumn?: SortColumnEvents;
  page?: number;
  size?: number;
}

export interface VehicleDailyAggregationTripsAndStopsRequest extends VehicleAggregationTripsAndStopsRequest {
  page?: number;
  size?: number;
}

export interface DriverDailyAggregationTripsAndStopsRequest extends DriverAggregationTripsAndStopsRequest {
  page?: number;
  size?: number;
}
